import type {
  BudgetRecapChange,
  BudgetRecapMer,
  BudgetRecapRequest,
  BudgetRecapTargets,
} from "~/ui-rtk/api/budgetRecommenderApi"
import type { ApplyResult, RecommendationRow, TroasChange } from "./types"
import { describeRecentChange } from "./budget-utils"

/**
 * Outcomes as if every applyable row in the snapshot had just been applied —
 * lets the review screen preview the recap before anything is sent, and
 * exercise the whole pipeline without touching a budget.
 */
export function previewResults(snapshot: RecapSnapshot): ApplyResult[] {
  return snapshot.rows
    .filter(row => row.canApplyBudget)
    .map(row => ({
      entityId: row.entityId,
      entityName: row.entityName,
      channel: row.channel,
      success: true,
      paused: row.shouldPause,
      ...(snapshot.troasChanges.has(row.campaignId)
        ? { troasApplied: true }
        : {}),
    }))
}

/**
 * What the approve was invoked with, frozen at the moment the user clicked
 * Approve & Apply. The parent clears its row selection (the modal's `rows`
 * prop) as soon as apply resolves, so the recap must read from this snapshot
 * rather than from props.
 */
export interface RecapSnapshot {
  rows: RecommendationRow[]
  /** The recommender inputs the rows were decided against. */
  targets: BudgetRecapTargets
  /** The (a)MER readout shown at approve time. */
  mer: BudgetRecapMer
  /** entityId → the daily budget that was sent (edit override or recommendation). */
  budgets: Map<string, number>
  /** campaignId → the combined tROAS move that was attempted. */
  troasChanges: Map<string, TroasChange>
  /**
   * Active rows deliberately left at their current budget (unchecked, or
   * labeled and left out). They go to the recap as no-op changes with a
   * reason, so the story can justify keeping them where they are.
   */
  unchangedRows?: RecommendationRow[]
}

/** The recap API's limit on `unchangedReason` (BudgetRecapChangeDto). */
const UNCHANGED_REASON_MAX = 300

/** Why a row was left at its current budget, in the recap's plain words. */
export function unchangedReasonFor(row: RecommendationRow): string {
  const labels = row.recentChanges?.map(describeRecentChange) ?? []
  // A row with no type is never selectable, so it only reaches the review
  // when the user overrode the initiative-type gate.
  if (row.category === null) {
    const base =
      "no initiative type; kept at its current budget, counted as demand capture"
    return labels.length > 0 ? `${base}; ${labels.join("; ")}` : base
  }
  if (labels.length > 0) {
    return `left out of this update; ${labels.join("; ")}`
  }
  return "not selected for this update"
}

/**
 * Join the approve's rows with their outcomes into the recap request. Manual
 * (read-only channel) rows never reached a platform and are left out; rows
 * that failed or were skipped are sent flagged so the server can ignore them.
 * Returns null when nothing was applied — there is no story to tell.
 */
export function buildRecapRequest(
  snapshot: RecapSnapshot,
  results: ApplyResult[],
  labelFor: (initiativeType: string) => string,
): BudgetRecapRequest | null {
  const byEntity = new Map(results.map(r => [r.entityId, r]))
  const changes: BudgetRecapChange[] = []

  for (const row of snapshot.rows) {
    if (!row.canApplyBudget) continue
    const result = byEntity.get(row.entityId)
    if (!result) continue

    const success = result.success && !result.skipped
    const paused = row.shouldPause
    const troas =
      result.troasApplied === true
        ? snapshot.troasChanges.get(row.campaignId)
        : undefined

    changes.push({
      entityId: row.entityId,
      entityName: row.entityName,
      entityType: row.entityType,
      campaignId: row.campaignId,
      channel: row.channel,
      initiativeType: row.initiativeType,
      initiativeTypeLabel: labelFor(row.initiativeType),
      category: row.category,
      analysisPeriodDays: row.analysisPeriodDays,
      dataThroughDate: row.dataThroughDate,
      periodSpend: row.periodSpend,
      avgDailySpend: row.avgDailySpend,
      drRoas: row.drRoas,
      brandRoas: row.brandRoas,
      modeledRevenuePeriod: row.modeledRevenuePeriod,
      brandValuePeriod: row.brandValuePeriod,
      currentDailyBudget: row.currentDailyBudget,
      newDailyBudget: paused
        ? null
        : (snapshot.budgets.get(row.entityId) ?? row.adjustedBudget),
      action: paused ? "pause" : "update",
      success,
      confidence: row.confidence,
      ...(troas
        ? {
            troas: {
              fromTargetRoas: troas.fromTargetRoas,
              toTargetRoas: troas.toTargetRoas,
            },
          }
        : {}),
    })
  }

  // No story without at least one applied change — the unchanged rows are
  // context for a story, not a story on their own.
  if (!changes.some(c => c.success)) return null

  // Rows left at their current budget go along as no-op changes with a
  // reason, so the recap can say why they were kept where they are.
  for (const row of snapshot.unchangedRows ?? []) {
    changes.push({
      entityId: row.entityId,
      entityName: row.entityName,
      entityType: row.entityType,
      campaignId: row.campaignId,
      channel: row.channel,
      initiativeType: row.initiativeType,
      initiativeTypeLabel: labelFor(row.initiativeType),
      category: row.category,
      analysisPeriodDays: row.analysisPeriodDays,
      dataThroughDate: row.dataThroughDate,
      periodSpend: row.periodSpend,
      avgDailySpend: row.avgDailySpend,
      drRoas: row.drRoas,
      brandRoas: row.brandRoas,
      modeledRevenuePeriod: row.modeledRevenuePeriod,
      brandValuePeriod: row.brandValuePeriod,
      currentDailyBudget: row.currentDailyBudget,
      newDailyBudget: row.currentDailyBudget,
      action: "update",
      success: true,
      confidence: row.confidence,
      // A row carrying several audited labels (each with its audit id) can
      // run past the API's limit, which would reject the whole recap.
      unchangedReason: unchangedReasonFor(row).slice(0, UNCHANGED_REASON_MAX),
    })
  }

  return {
    targets: snapshot.targets,
    ...(snapshot.mer.merLabel ? { mer: snapshot.mer } : {}),
    changes,
  }
}
